import { useCallback, useEffect, useRef, useState } from 'react';
import { scheduleService } from '@/services/schedule.service';
import { useBottomToastStore } from '@/stores/bottomToastStore';
import { toast } from '@/stores/toastStore';
import type { TaskStatus } from '@/types';

/** Production baseline top toast for a failed status write (YAR-476). */
export const TASK_UPDATE_ERROR = 'Could not update the task. Please try again.';

export interface CompletableTask {
  id: string;
  title: string;
  status: string;
}

const TASK_STATUSES: readonly TaskStatus[] = [
  'TODO',
  'IN_PROGRESS',
  'DONE',
  'CANCELLED',
];

function asTaskStatus(value: string): TaskStatus {
  return TASK_STATUSES.find((s) => s === value) ?? 'TODO';
}

function isClosed(status: string) {
  return status === 'DONE' || status === 'CANCELLED';
}

interface UseTaskCompletionOptions {
  /**
   * Re-read the page (and the chrome) after a write lands. Should NOT flash
   * a skeleton: the row already moved optimistically.
   */
  onChanged: () => Promise<void> | void;
}

/**
 * Completing a task on Today / Schedule, with Undo (YAR-476).
 *
 * - `complete` marks the task DONE in local state first, so it leaves the
 *   list at once, raises the undo toast, and saves. A failed save puts the
 *   row back and shows the existing top toast.
 * - The status the task had BEFORE completion is remembered, so Undo writes
 *   that back — an IN_PROGRESS task comes back IN_PROGRESS, not TODO.
 * - `restore` is Undo, and also what unchecking a task in the detail modal
 *   does. With no remembered status (the task was done before this screen
 *   saw it) it falls back to TODO, the History rule.
 *
 * Pages render `applyOverrides(rows)` so optimistic statuses win over the
 * last fetch until the refetch lands.
 */
export function useTaskCompletion({ onChanged }: UseTaskCompletionOptions) {
  const [overrides, setOverrides] = useState<Map<string, TaskStatus>>(
    () => new Map(),
  );
  const previousStatus = useRef(new Map<string, TaskStatus>());
  // The in-flight completion per task, so an Undo tapped before the save
  // lands waits for it instead of racing it.
  const pendingSave = useRef(new Map<string, Promise<boolean>>());
  const toastIdByTask = useRef(new Map<string, string>());
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const setOverride = useCallback((id: string, status: TaskStatus | null) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      if (status) next.set(id, status);
      else next.delete(id);
      return next;
    });
  }, []);

  const dismissToastFor = useCallback((taskId: string) => {
    const toastId = toastIdByTask.current.get(taskId);
    if (!toastId) return;
    toastIdByTask.current.delete(taskId);
    useBottomToastStore.getState().dismissAction(toastId);
  }, []);

  const restore = useCallback(
    async (taskId: string) => {
      dismissToastFor(taskId);
      const saved = await pendingSave.current.get(taskId);
      // The completion never landed — the row is already back.
      if (saved === false) return;
      const target = previousStatus.current.get(taskId) ?? 'TODO';
      setOverride(taskId, target);
      try {
        await scheduleService.setTaskStatus(taskId, target);
        previousStatus.current.delete(taskId);
        await onChangedRef.current();
      } catch (err) {
        console.error('Failed to restore task:', err);
        toast.error(TASK_UPDATE_ERROR);
      } finally {
        setOverride(taskId, null);
      }
    },
    [dismissToastFor, setOverride],
  );

  const complete = useCallback(
    async (task: CompletableTask) => {
      if (isClosed(task.status)) return;
      const previous = asTaskStatus(task.status);
      previousStatus.current.set(task.id, previous);
      setOverride(task.id, 'DONE');

      const toastId = useBottomToastStore.getState().showAction({
        name: task.title,
        suffix: 'completed',
        actionLabel: 'Undo',
        onAction: () => void restore(task.id),
        onEnd: () => {
          if (toastIdByTask.current.get(task.id) === toastId) {
            toastIdByTask.current.delete(task.id);
          }
        },
      });
      toastIdByTask.current.set(task.id, toastId);

      const save = scheduleService
        .setTaskStatus(task.id, 'DONE')
        .then(() => true)
        .catch((err: unknown) => {
          console.error('Failed to complete task:', err);
          return false;
        });
      pendingSave.current.set(task.id, save);
      const ok = await save;
      pendingSave.current.delete(task.id);

      if (!ok) {
        dismissToastFor(task.id);
        previousStatus.current.delete(task.id);
        setOverride(task.id, null);
        toast.error(TASK_UPDATE_ERROR);
        return;
      }
      try {
        await onChangedRef.current();
      } finally {
        // An Undo may already have put its own override in place.
        setOverrides((prev) => {
          if (prev.get(task.id) !== 'DONE') return prev;
          const next = new Map(prev);
          next.delete(task.id);
          return next;
        });
      }
    },
    [dismissToastFor, restore, setOverride],
  );

  /**
   * The checkbox handler: closed → restore, open → complete. `status` is
   * what the caller believes the task is now (the detail modal passes its
   * own checkbox state).
   */
  const toggle = useCallback(
    (task: CompletableTask) =>
      isClosed(task.status) ? restore(task.id) : complete(task),
    [complete, restore],
  );

  const applyOverrides = useCallback(
    <T extends { id: string; status: string }>(rows: T[]): T[] =>
      overrides.size === 0
        ? rows
        : rows.map((row) => {
            const status = overrides.get(row.id);
            return status ? { ...row, status } : row;
          }),
    [overrides],
  );

  return { complete, restore, toggle, applyOverrides };
}
